/* Visa-Erfassung: Regionen, Dauer, Status und die Ländersuche.
 *
 * Ein Visum ist ein Eintrag in player.visas[]. Neu sind die Felder
 *   region   'schengen' | 'uk' | 'usa' | 'turkey' | 'other'
 *   country  nur bei 'other': Ländername, immer auf Englisch gespeichert
 *   term     'short' | 'long'
 * `type` bleibt als lesbarer Text erhalten (z. B. "EU/Schengen, Short term").
 * Die Anzeige und die Benachrichtigungen kennen weiterhin nur `type` und
 * `status`. Alte Einträge haben nur `type` — sie bleiben unverändert lesbar.
 *
 * Der gespeicherte Text ist bewusst englisch und sprachneutral. Die
 * Oberfläche baut ihre Beschriftung aus region/term in der Sprache des
 * Betrachters und fällt bei alten Einträgen auf `type` zurück.
 */

export const VISA_REGIONS = ['schengen', 'uk', 'usa', 'turkey', 'other'];
export const VISA_TERMS = ['short', 'long'];
export const VISA_STATUSES = ['applied', 'granted', 'rejected'];

const REGION_TEXT = { schengen: 'EU/Schengen', uk: 'UK', usa: 'USA', turkey: 'Turkey' };
const TERM_TEXT = { short: 'Short term', long: 'Long term' };

/** Lesbarer Text für das Feld `type`, z. B. "EU/Schengen, Short term". */
export function visaTypeText({ region, country, term } = {}) {
  const place = region === 'other' ? String(country || '').trim() : REGION_TEXT[region] || '';
  return [place, TERM_TEXT[term] || ''].filter(Boolean).join(', ');
}

/** Reicht der Entwurf, um ein Visum anzulegen? Region und Dauer sind Pflicht. */
export function isVisaDraftValid({ region, country, term } = {}) {
  if (!VISA_REGIONS.includes(region) || !VISA_TERMS.includes(term)) return false;
  if (region === 'other' && !String(country || '').trim()) return false;
  return true;
}

/** Neuer Eintrag für player.visas — alle bisherigen Felder mit Vorgabewert. */
export function newVisa(draft, stamp = {}) {
  const country = draft.region === 'other' ? String(draft.country || '').trim() : '';
  const status = draft.status || 'applied';
  /* Zeitraum, Ausstellung und Einreise gehören nur zu "granted". Bei den
     anderen Zuständen werden eingegebene Werte nicht übernommen. */
  const g = status === 'granted' ? draft : {};
  return {
    type: visaTypeText({ ...draft, country }),
    region: draft.region,
    country,
    term: draft.term,
    status,
    duration: g.duration || '', multi: !!g.multi,
    from: g.from || '', until: g.until || '', issued: g.issued || '', appdate: g.appdate || '',
    entered: '', leave: '',
    files: [],
    ...stamp,
  };
}

/** `type` nach einer Änderung an Region, Land oder Dauer neu setzen. */
export function syncVisaType(visa) {
  const text = visaTypeText(visa);
  if (text) visa.type = text;
}

/* --- Länderliste -------------------------------------------------------- */

/* ISO-3166-Alpha-2. Die Namen kommen aus Intl.DisplayNames, nicht aus einer
   eigenen Übersetzungstabelle — so stimmt die Liste in jeder Sprache. */
const COUNTRY_CODES =
  ('AF AL DZ AD AO AG AR AM AU AT AZ BS BH BD BB BY BE BZ BJ BT BO BA BW BR BN BG BF BI KH CM CA CV CF TD CL CN ' +
   'CO KM CG CD CR CI HR CU CY CZ DK DJ DM DO EC EG SV GQ ER EE SZ ET FJ FI FR GA GM GE DE GH GR GD GT GN GW GY ' +
   'HT HN HU IS IN ID IR IQ IE IL IT JM JP JO KZ KE KI KP KR XK KW KG LA LV LB LS LR LY LI LT LU MG MW MY MV ML ' +
   'MT MH MR MU MX FM MD MC MN ME MA MZ MM NA NR NP NL NZ NI NE NG MK NO OM PK PW PS PA PG PY PE PH PL PT QA RO ' +
   'RU RW KN LC VC WS SM ST SA SN RS SC SL SG SK SI SB SO ZA SS ES LK SD SR SE CH SY TW TJ TZ TH TL TG TO TT TN ' +
   'TR TM TV UG UA AE GB US UY UZ VU VA VE VN YE ZM ZW').split(' ');

const norm = (s) => String(s || '').trim().toLocaleLowerCase();

/** [{ code, name }] in der Sprache `locale`, alphabetisch. */
export function countryNames(locale = 'en') {
  let dn;
  try {
    dn = new Intl.DisplayNames([locale], { type: 'region' });
  } catch {
    dn = null;
  }
  return COUNTRY_CODES
    .map((code) => ({ code, name: (dn && dn.of(code)) || code }))
    .sort((a, b) => a.name.localeCompare(b.name, locale));
}

/** Treffer für eine Sucheingabe: erst Namen, die damit beginnen, dann die, die es enthalten. */
export function filterCountries(list, query, limit = 8) {
  const q = norm(query);
  if (!q) return [];
  const starts = [];
  const contains = [];
  for (const c of list) {
    const n = norm(c.name);
    if (n.startsWith(q)) starts.push(c);
    else if (n.includes(q)) contains.push(c);
  }
  return [...starts, ...contains].slice(0, limit);
}

/**
 * Was gespeichert wird: Passt die Eingabe zu einem Land der Liste (in der
 * Oberflächensprache oder auf Englisch), kommt der englische Name; sonst der
 * eingegebene Text unverändert (Freitext als Rückfall).
 */
export function storedCountryName(input, locale = 'en') {
  const text = String(input || '').trim();
  if (!text) return '';
  const q = norm(text);
  const local = countryNames(locale).find((c) => norm(c.name) === q);
  const english = countryNames('en');
  if (local) return english.find((c) => c.code === local.code).name;
  const en = english.find((c) => norm(c.name) === q);
  return en ? en.name : text;
}

/** Gespeicherten (englischen) Namen in der Sprache `locale` anzeigen; Freitext bleibt. */
export function displayCountryName(stored, locale = 'en') {
  const text = String(stored || '').trim();
  if (!text) return '';
  const hit = countryNames('en').find((c) => norm(c.name) === norm(text));
  if (!hit) return text;
  return countryNames(locale).find((c) => c.code === hit.code).name;
}
